import type { DataGridColumnOrderState } from '@ibsys/kit';
import { useCallback, useMemo } from 'react';
import type { DataGridColumnStateProps } from '@/entities/settings';

/** Параметры вычисления эффективного порядка колонок */
type Params = {
  /** Пропы колонок грида из `useDialogColumnsState` */
  gridColumnProps: DataGridColumnStateProps;
  /** Отсекает из порядка id колонок поиска по содержимому (в пользовательский порядок не входят) */
  filterUserColumnOrder: (columnOrder: string[]) => string[];
  /** Ставит пару колонок поиска по содержимому в начало пользовательского порядка */
  getEffectiveColumnOrder: (userColumnOrder: string[]) => string[];
};

/** Эффективный порядок колонок и пропы колонок с ним */
type Result = {
  effectiveColumnOrder: DataGridColumnOrderState;
  effectiveGridColumnProps: DataGridColumnStateProps;
};

/**
 * Эффективный порядок колонок при активном режиме поиска по содержимому: пара «Релевантность +
 * фрагмент» выводится в самом начале таблицы и не зависит от пользовательского сохранённого
 * `columnOrder`; в localStorage эти id не сохраняются, чтобы не «прилипать» к остальному порядку
 * после деактивации режима.
 */
export const useEffectiveColumnOrder = ({
  gridColumnProps,
  filterUserColumnOrder,
  getEffectiveColumnOrder,
}: Params): Result => {
  const userColumnOrder = useMemo(
    () => filterUserColumnOrder(gridColumnProps.columnOrder ?? []),
    [gridColumnProps.columnOrder, filterUserColumnOrder],
  );

  const effectiveColumnOrder = useMemo<DataGridColumnOrderState>(
    () => getEffectiveColumnOrder(userColumnOrder),
    [userColumnOrder, getEffectiveColumnOrder],
  );

  const handleEffectiveColumnOrderChange = useCallback(
    (next: DataGridColumnOrderState) => {
      // Сохраняем только пользовательскую часть порядка; id колонок поиска по содержимому управляются автоматически
      gridColumnProps.onColumnOrderChange(filterUserColumnOrder(next));
    },
    [gridColumnProps, filterUserColumnOrder],
  );

  const effectiveGridColumnProps = useMemo(
    () => ({
      ...gridColumnProps,
      columnOrder: effectiveColumnOrder,
      onColumnOrderChange: handleEffectiveColumnOrderChange,
    }),
    [gridColumnProps, effectiveColumnOrder, handleEffectiveColumnOrderChange],
  );

  return { effectiveColumnOrder, effectiveGridColumnProps };
};
